import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
    BillingInterval,
    DashboardFeature,
    DashboardFeatureAccessByRole,
    SelfServicePlanCode,
} from '../interfaces/Frameworks';

export type { BillingInterval, SelfServicePlanCode } from '../interfaces/Frameworks';

export type PlanUsageLimits = {
    audioScribeRecordings: number | null;
    aiGeneratedReports: number | null;
};

export type PublicPlan = {
    planCode: SelfServicePlanCode;
    versionId: string;
    name: string;
    description: string;
    publiclyPurchasable: boolean;
    displayOrder: number;
    highlighted: boolean;
    currency: 'GBP';
    monthlyDisplayPrice?: number;
    annualDisplayPrice?: number;
    pricesIncludeTax: false;
    dashboardSeatsPerUnit: number;
    familyLicensesPerUnit: number;
    minimumUnits: number;
    maximumSelfServeUnits: number;
    usageLimits: PlanUsageLimits;
    allowedDashboardFeatures: DashboardFeature[];
    defaultAccessByRole: DashboardFeatureAccessByRole;
};

export type PublicPlanCatalog = {
    plans: PublicPlan[];
    publishedAt?: unknown;
};

export type EditablePlanCatalogVersion = Omit<PublicPlan, 'versionId'> & {
    versionId: string;
    monthlyStripePriceId?: string;
    annualStripePriceId?: string;
};

export type PlanCatalogAdminVersion = EditablePlanCatalogVersion & {
    status: 'draft' | 'published' | 'retired';
    effectiveFrom?: string;
    revision: number;
    updatedAt?: string;
    publishedAt?: string;
};

export type PlanCatalogAdminState = {
    speechNeedOptions: Array<{ id: string; name: string }>;
    plans: Array<{
        planCode: SelfServicePlanCode;
        activeVersionId: string | null;
        speechNeeds: string[] | null;
        incompatibleVersionIds: string[];
        versions: PlanCatalogAdminVersion[];
    }>;
};

export type SelfServeSignupRequest = {
    planCode: SelfServicePlanCode;
    versionId: string;
    billingInterval: BillingInterval;
    providerName: string;
    billingOwnerName?: string;
    subscriptionUnits: number;
};

export type SelfServeSignupResponse = {
    checkoutIntentId: string;
    providerId: string;
    status: 'provisioned' | 'requires_checkout';
    checkoutUrl?: string;
};

export type SelfServeSignupAccountStatus =
    | 'eligible'
    | 'existing_self_service_owner'
    | 'existing_dashboard_provider'
    | 'inconsistent';

export type SelfServeSignupEligibility = {
    accountStatus: SelfServeSignupAccountStatus;
    providerId?: string;
    providerName?: string;
    currentPlanCode?: SelfServicePlanCode;
    clinicName?: {
        available: boolean;
        normalizedName: string;
    };
};

export type ProvisioningStatus = {
    checkoutIntentId: string;
    providerId?: string;
    status: 'pending' | 'checkout_created' | 'pending_payment' | 'payment_confirmed' |
        'provisioned' | 'failed' | 'expired' | 'manual_recovery';
    intentKind?: 'self_serve_signup' | 'existing_provider_migration';
    planCode?: SelfServicePlanCode;
    errorMessage?: string;
};

export type ExistingProviderPremiumMigrationPreview = {
    healthcareProviderId: string;
    providerName: string;
    eligible: true;
    planCode: 'premium';
    planVersionId: string;
    dashboardSeatsUsed: number;
    registeredDashboardUserCount: number;
    existingFamilies: number;
    minimumSubscriptionUnits: number;
    maximumSelfServeUnits: number;
    dashboardSeatsPerUnit: number;
    familyLicensesPerUnit: number;
    monthlyUnitPrice: number;
    annualUnitPrice: number;
    currency: 'GBP';
    pricesIncludeTax: false;
    offerEnabled: boolean;
    offerCurrent: boolean;
    offerRevision: number;
    mayCheckout: boolean;
    currentAdminTherapistId?: string;
    validationToken?: string;
    approvedAt?: string;
    approvedByEmail?: string;
    activeAdmins: Array<{
        therapistId: string;
        name: string;
        email: string;
    }>;
};

export type ExistingProviderPremiumMigrationOfferState = {
    healthcareProviderId: string;
    offerEnabled: boolean;
    offerCurrent: boolean;
    offerRevision: number;
    offerStatus?: 'disabled' | 'enabled' | 'enabling' | 'disabling' |
        'enable_pending' | 'disable_pending' | 'payment_pending' | 'completed' | 'manual_recovery';
    approvedAt?: string;
    approvedByEmail?: string;
};

const premiumMigrationOfferStatuses = [
    'disabled', 'enabled', 'enabling', 'disabling', 'enable_pending', 'disable_pending',
    'payment_pending', 'completed', 'manual_recovery',
] as const;

export type ExistingProviderPremiumCheckoutRequest = {
    billingOwnerTherapistId: string;
    billingInterval: BillingInterval;
    subscriptionUnits: number;
};

export type ExistingProviderPremiumCheckoutResponse = {
    healthcareProviderId: string;
    checkoutIntentId: string;
    status: 'requires_checkout';
    checkoutUrl: string;
};

const planCodes: SelfServicePlanCode[] = ['starter', 'premium'];
const dashboardFeatures: DashboardFeature[] = [
    'clients',
    'audio_scribe_reports',
    'messaging',
    'development',
    'provider_selector',
    'organisation_analytics_heatmap',
    'organisation_team',
    'organisation_team_manage',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === 'string');

const optionalString = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value !== 'string' || !value) {
        throw new Error('The plan catalogue administration data is invalid.');
    }
    return value;
};

const parseUsageLimit = (value: unknown): number | null => {
    if (value === null) return null;
    if (!Number.isInteger(value) || (value as number) < 0) {
        throw new Error('The published plan catalogue is invalid. Purchasing has been disabled.');
    }
    return value as number;
};

// Validate commerce data at the client boundary so malformed remote prices never reach checkout.
const parsePlan = (value: unknown): PublicPlan => {
    if (!isRecord(value) ||
        !planCodes.includes(value.planCode as SelfServicePlanCode) ||
        typeof value.versionId !== 'string' || !value.versionId ||
        typeof value.name !== 'string' || !value.name ||
        typeof value.description !== 'string' ||
        typeof value.publiclyPurchasable !== 'boolean' ||
        !isFiniteNumber(value.displayOrder) ||
        typeof value.highlighted !== 'boolean' ||
        value.currency !== 'GBP' ||
        value.pricesIncludeTax !== false ||
        !Number.isInteger(value.dashboardSeatsPerUnit) || (value.dashboardSeatsPerUnit as number) < 1 ||
        !Number.isInteger(value.familyLicensesPerUnit) || (value.familyLicensesPerUnit as number) < 1 ||
        !Number.isInteger(value.minimumUnits) || (value.minimumUnits as number) < 1 ||
        !Number.isInteger(value.maximumSelfServeUnits) ||
        (value.maximumSelfServeUnits as number) < (value.minimumUnits as number) ||
        !isRecord(value.usageLimits) ||
        !isStringArray(value.allowedDashboardFeatures) ||
        value.allowedDashboardFeatures.some((feature) => !dashboardFeatures.includes(feature as DashboardFeature)) ||
        !isRecord(value.defaultAccessByRole)) {
        throw new Error('The published plan catalogue is invalid. Purchasing has been disabled.');
    }

    for (const roleFeatures of Object.values(value.defaultAccessByRole)) {
        if (!isRecord(roleFeatures) || Object.values(roleFeatures).some((enabled) => typeof enabled !== 'boolean')) {
            throw new Error('The published plan catalogue is invalid. Purchasing has been disabled.');
        }
    }

    const parseDisplayPrice = (price: unknown): number | undefined => {
        if (price === undefined) return undefined;
        if (!isFiniteNumber(price) || price < 0) {
            throw new Error('The published plan catalogue is invalid. Purchasing has been disabled.');
        }
        return price;
    };

    return {
        planCode: value.planCode as SelfServicePlanCode,
        versionId: value.versionId as string,
        name: value.name as string,
        description: value.description as string,
        publiclyPurchasable: value.publiclyPurchasable as boolean,
        displayOrder: value.displayOrder as number,
        highlighted: value.highlighted as boolean,
        currency: 'GBP',
        monthlyDisplayPrice: parseDisplayPrice(value.monthlyDisplayPrice),
        annualDisplayPrice: parseDisplayPrice(value.annualDisplayPrice),
        pricesIncludeTax: false,
        dashboardSeatsPerUnit: value.dashboardSeatsPerUnit as number,
        familyLicensesPerUnit: value.familyLicensesPerUnit as number,
        minimumUnits: value.minimumUnits as number,
        maximumSelfServeUnits: value.maximumSelfServeUnits as number,
        usageLimits: {
            audioScribeRecordings: parseUsageLimit(value.usageLimits.audioScribeRecordings),
            aiGeneratedReports: parseUsageLimit(value.usageLimits.aiGeneratedReports),
        },
        allowedDashboardFeatures: value.allowedDashboardFeatures as DashboardFeature[],
        defaultAccessByRole: value.defaultAccessByRole as DashboardFeatureAccessByRole,
    };
};

// Keep private Price IDs on the Root-only path while validating the same public plan fields.
const parseAdminVersion = (value: unknown): PlanCatalogAdminVersion => {
    if (!isRecord(value)) {
        throw new Error('The plan catalogue administration data is invalid.');
    }
    const publicPlan = parsePlan(value);
    const status = value.status;
    const revision = value.revision;
    if (!['draft', 'published', 'retired'].includes(status as string) ||
        !Number.isInteger(revision) || (revision as number) < 0) {
        throw new Error('The plan catalogue administration data is invalid.');
    }

    return {
        ...publicPlan,
        monthlyStripePriceId: optionalString(value.monthlyStripePriceId),
        annualStripePriceId: optionalString(value.annualStripePriceId),
        status: status as PlanCatalogAdminVersion['status'],
        effectiveFrom: optionalString(value.effectiveFrom),
        revision: revision as number,
        updatedAt: optionalString(value.updatedAt),
        publishedAt: optionalString(value.publishedAt),
    };
};

const loadPlanCatalog = async (
    request: { previewVersionIds?: Partial<Record<SelfServicePlanCode, string>> },
    includeNonPublic: boolean
): Promise<PublicPlanCatalog> => {
    const callable = httpsCallable<typeof request, unknown>(getFunctions(), 'getPublicPlanCatalog');
    const result = await callable(request);
    if (!isRecord(result.data) || !Array.isArray(result.data.plans)) {
        throw new Error('Plans are temporarily unavailable. Please try again shortly.');
    }

    const plans = result.data.plans
        .map(parsePlan)
        .filter((plan) => includeNonPublic || plan.publiclyPurchasable)
        .sort((left, right) => left.displayOrder - right.displayOrder);
    if (plans.length === 0) {
        throw new Error('Plans are temporarily unavailable. Please try again shortly.');
    }

    return { plans, publishedAt: result.data.publishedAt };
};

export const getPublicPlanCatalog = async (): Promise<PublicPlanCatalog> =>
    loadPlanCatalog({}, false);

export const getPlanCatalogPreview = async (
    previewVersionIds: Partial<Record<SelfServicePlanCode, string>>
): Promise<PublicPlanCatalog> => loadPlanCatalog({ previewVersionIds }, true);

export const getPlanCatalogAdminState = async (): Promise<PlanCatalogAdminState> => {
    const callable = httpsCallable<Record<string, never>, unknown>(getFunctions(), 'getPlanCatalogAdminState');
    const result = await callable({});
    if (!isRecord(result.data) || !Array.isArray(result.data.plans) || !Array.isArray(result.data.speechNeedOptions)) {
        throw new Error('The plan catalogue administration data is invalid.');
    }

    const seenSpeechNeedIds = new Set<string>();
    const speechNeedOptions = result.data.speechNeedOptions.map((option) => {
        if (!isRecord(option) || typeof option.id !== 'string' || !option.id ||
            typeof option.name !== 'string' || !option.name || seenSpeechNeedIds.has(option.id)) {
            throw new Error('The plan catalogue administration data is invalid.');
        }
        seenSpeechNeedIds.add(option.id);
        return { id: option.id, name: option.name };
    });

    const seenPlanCodes = new Set<SelfServicePlanCode>();
    const plans = result.data.plans.map((planState) => {
        if (!isRecord(planState) || !planCodes.includes(planState.planCode as SelfServicePlanCode) ||
            (planState.activeVersionId !== null && typeof planState.activeVersionId !== 'string') ||
            (planState.speechNeeds !== undefined && planState.speechNeeds !== null && !isStringArray(planState.speechNeeds)) ||
            (planState.incompatibleVersionIds !== undefined && !isStringArray(planState.incompatibleVersionIds)) ||
            !Array.isArray(planState.versions)) {
            throw new Error('The plan catalogue administration data is invalid.');
        }
        const planCode = planState.planCode as SelfServicePlanCode;
        if (seenPlanCodes.has(planCode)) {
            throw new Error('The plan catalogue administration data is invalid.');
        }
        seenPlanCodes.add(planCode);
        const versions = planState.versions.map(parseAdminVersion);
        if (versions.some((version) => version.planCode !== planCode)) {
            throw new Error('The plan catalogue administration data is invalid.');
        }
        return {
            planCode,
            activeVersionId: planState.activeVersionId as string | null,
            // Missing legacy parent configuration remains visibly unconfigured.
            speechNeeds: planState.speechNeeds === undefined || planState.speechNeeds === null ?
                null : [...planState.speechNeeds],
            incompatibleVersionIds: Array.isArray(planState.incompatibleVersionIds) ?
                [...planState.incompatibleVersionIds] : [],
            versions,
        };
    });
    if (plans.length !== planCodes.length || planCodes.some((planCode) => !seenPlanCodes.has(planCode))) {
        throw new Error('The plan catalogue administration data is invalid.');
    }
    return { plans, speechNeedOptions };
};

export const updatePlanSpeechNeeds = async (
    planCode: SelfServicePlanCode,
    speechNeeds: string[]
): Promise<{ planCode: SelfServicePlanCode; speechNeeds: string[]; updatedProviders: number }> => {
    const callable = httpsCallable<
        { planCode: SelfServicePlanCode; speechNeeds: string[] },
        { planCode: SelfServicePlanCode; speechNeeds: string[]; updatedProviders: number }
    >(getFunctions(), 'updatePlanSpeechNeeds');
    const result = await callable({ planCode, speechNeeds });
    return result.data;
};

export const savePlanCatalogDraft = async (
    planCode: SelfServicePlanCode,
    versionId: string,
    version: EditablePlanCatalogVersion
): Promise<{ planCode: SelfServicePlanCode; versionId: string; valid: true }> => {
    const callable = httpsCallable<
        { planCode: SelfServicePlanCode; versionId: string; version: EditablePlanCatalogVersion },
        { planCode: SelfServicePlanCode; versionId: string; valid: true }
    >(getFunctions(), 'savePlanCatalogDraft');
    const result = await callable({ planCode, versionId, version });
    return result.data;
};

export const validatePlanCatalogVersion = async (
    planCode: SelfServicePlanCode,
    versionId: string,
    verifyStripe: boolean
): Promise<{ planCode: SelfServicePlanCode; versionId: string; valid: true; preview: PublicPlan }> => {
    const callable = httpsCallable<
        { planCode: SelfServicePlanCode; versionId: string; verifyStripe: boolean },
        { planCode: SelfServicePlanCode; versionId: string; valid: true; preview: PublicPlan }
    >(getFunctions(), 'validatePlanCatalogVersion');
    const result = await callable({ planCode, versionId, verifyStripe });
    return result.data;
};

export const publishPlanCatalogVersion = async (
    planCode: SelfServicePlanCode,
    versionId: string
): Promise<{ planCode: SelfServicePlanCode; versionId: string; published: true }> => {
    const callable = httpsCallable<
        { planCode: SelfServicePlanCode; versionId: string },
        { planCode: SelfServicePlanCode; versionId: string; published: true }
    >(getFunctions(), 'publishPlanCatalogVersion');
    const result = await callable({ planCode, versionId });
    return result.data;
};

export const createSelfServeSignup = async (
    request: SelfServeSignupRequest
): Promise<SelfServeSignupResponse> => {
    const callable = httpsCallable<SelfServeSignupRequest, SelfServeSignupResponse>(
        getFunctions(),
        'createSelfServeSignup'
    );
    const result = await callable(request);
    return result.data;
};

// Check authenticated account eligibility before showing or submitting the clinic form.
export const getSelfServeSignupEligibility = async (
    providerName?: string
): Promise<SelfServeSignupEligibility> => {
    const request = providerName ? { providerName } : {};
    const callable = httpsCallable<typeof request, unknown>(
        getFunctions(),
        'getSelfServeSignupEligibility'
    );
    const result = await callable(request);
    const value = result.data;
    const validStatuses: SelfServeSignupAccountStatus[] = [
        'eligible',
        'existing_self_service_owner',
        'existing_dashboard_provider',
        'inconsistent',
    ];
    if (!isRecord(value) || !validStatuses.includes(value.accountStatus as SelfServeSignupAccountStatus) ||
        (value.providerId !== undefined && typeof value.providerId !== 'string') ||
        (value.providerName !== undefined && typeof value.providerName !== 'string') ||
        (value.currentPlanCode !== undefined && !planCodes.includes(value.currentPlanCode as SelfServicePlanCode)) ||
        (value.clinicName !== undefined && (
            !isRecord(value.clinicName) ||
            typeof value.clinicName.available !== 'boolean' ||
            typeof value.clinicName.normalizedName !== 'string'
        ))) {
        throw new Error('Signup eligibility could not be verified. Please try again.');
    }
    return value as SelfServeSignupEligibility;
};

export const createSubscriptionCheckoutSession = async (
    checkoutIntentId: string
): Promise<{ checkoutIntentId: string; checkoutUrl: string }> => {
    const callable = httpsCallable<
        { checkoutIntentId: string },
        { checkoutIntentId: string; checkoutUrl: string }
    >(getFunctions(), 'createSubscriptionCheckoutSession');
    const result = await callable({ checkoutIntentId });
    return result.data;
};

export const getProvisioningStatus = async (
    checkoutIntentId: string
): Promise<ProvisioningStatus> => {
    const callable = httpsCallable<{ checkoutIntentId: string }, ProvisioningStatus>(
        getFunctions(),
        'getProvisioningStatus'
    );
    const result = await callable({ checkoutIntentId });
    return result.data;
};

// Root-only migration preview keeps legacy providers unchanged while sizing their future Premium subscription.
export const getExistingProviderPremiumMigrationPreview = async (
    healthcareProviderId: string
): Promise<ExistingProviderPremiumMigrationPreview> => {
    const callable = httpsCallable<{ healthcareProviderId: string }, unknown>(
        getFunctions(),
        'getExistingProviderPremiumMigrationPreview'
    );
    const result = await callable({ healthcareProviderId });
    const value = result.data;
    const isNonNegativeInteger = (candidate: unknown): candidate is number =>
        Number.isInteger(candidate) && (candidate as number) >= 0;
    const isPositiveInteger = (candidate: unknown): candidate is number =>
        Number.isInteger(candidate) && (candidate as number) >= 1;
    const isNonNegativePrice = (candidate: unknown): candidate is number =>
        isFiniteNumber(candidate) && candidate >= 0;

    if (!isRecord(value) ||
        value.healthcareProviderId !== healthcareProviderId ||
        typeof value.providerName !== 'string' || !value.providerName ||
        value.eligible !== true ||
        value.planCode !== 'premium' ||
        typeof value.planVersionId !== 'string' || !value.planVersionId ||
        !isNonNegativeInteger(value.dashboardSeatsUsed) ||
        !isNonNegativeInteger(value.registeredDashboardUserCount) ||
        !isNonNegativeInteger(value.existingFamilies) ||
        !isPositiveInteger(value.minimumSubscriptionUnits) ||
        !isPositiveInteger(value.maximumSelfServeUnits) ||
        (value.minimumSubscriptionUnits as number) > (value.maximumSelfServeUnits as number) ||
        !isPositiveInteger(value.dashboardSeatsPerUnit) ||
        !isPositiveInteger(value.familyLicensesPerUnit) ||
        !isNonNegativePrice(value.monthlyUnitPrice) ||
        !isNonNegativePrice(value.annualUnitPrice) ||
        value.currency !== 'GBP' ||
        value.pricesIncludeTax !== false ||
        typeof value.offerEnabled !== 'boolean' ||
        typeof value.offerCurrent !== 'boolean' ||
        !isNonNegativeInteger(value.offerRevision) ||
        typeof value.mayCheckout !== 'boolean' ||
        (value.mayCheckout && (typeof value.currentAdminTherapistId !== 'string' ||
            !value.currentAdminTherapistId)) ||
        (!value.mayCheckout && (typeof value.validationToken !== 'string' || !value.validationToken)) ||
        !Array.isArray(value.activeAdmins) || value.activeAdmins.length === 0) {
        throw new Error('The Premium migration preview is invalid. Please reload and try again.');
    }

    const activeAdmins = value.activeAdmins.map((candidate) => {
        if (!isRecord(candidate) ||
            typeof candidate.therapistId !== 'string' || !candidate.therapistId ||
            typeof candidate.name !== 'string' || !candidate.name ||
            typeof candidate.email !== 'string' || !candidate.email) {
            throw new Error('The Premium migration preview is invalid. Please reload and try again.');
        }
        return {
            therapistId: candidate.therapistId,
            name: candidate.name,
            email: candidate.email,
        };
    });

    return {
        healthcareProviderId,
        providerName: value.providerName as string,
        eligible: true,
        planCode: 'premium',
        planVersionId: value.planVersionId as string,
        dashboardSeatsUsed: value.dashboardSeatsUsed as number,
        registeredDashboardUserCount: value.registeredDashboardUserCount as number,
        existingFamilies: value.existingFamilies as number,
        minimumSubscriptionUnits: value.minimumSubscriptionUnits as number,
        maximumSelfServeUnits: value.maximumSelfServeUnits as number,
        dashboardSeatsPerUnit: value.dashboardSeatsPerUnit as number,
        familyLicensesPerUnit: value.familyLicensesPerUnit as number,
        monthlyUnitPrice: value.monthlyUnitPrice as number,
        annualUnitPrice: value.annualUnitPrice as number,
        currency: 'GBP',
        pricesIncludeTax: false,
        offerEnabled: value.offerEnabled,
        offerCurrent: value.offerCurrent,
        offerRevision: value.offerRevision,
        mayCheckout: value.mayCheckout,
        currentAdminTherapistId: optionalString(value.currentAdminTherapistId),
        validationToken: optionalString(value.validationToken),
        approvedAt: optionalString(value.approvedAt),
        approvedByEmail: optionalString(value.approvedByEmail),
        activeAdmins,
    };
};

// Read the exposure switch independently so Root can always disable a stale or newly-invalid offer.
export const getExistingProviderPremiumMigrationOfferState = async (
    healthcareProviderId: string
): Promise<ExistingProviderPremiumMigrationOfferState> => {
    const callable = httpsCallable<{ healthcareProviderId: string }, unknown>(
        getFunctions(),
        'getExistingProviderPremiumMigrationOfferState'
    );
    const result = await callable({ healthcareProviderId });
    const value = result.data;
    if (!isRecord(value) || value.healthcareProviderId !== healthcareProviderId ||
        typeof value.offerEnabled !== 'boolean' || typeof value.offerCurrent !== 'boolean' ||
        !Number.isInteger(value.offerRevision) || (value.offerRevision as number) < 0 ||
        (value.offerStatus !== undefined &&
            !premiumMigrationOfferStatuses.includes(value.offerStatus as typeof premiumMigrationOfferStatuses[number]))) {
        throw new Error('The Premium migration offer state is invalid. Please reload and try again.');
    }
    return {
        healthcareProviderId,
        offerEnabled: value.offerEnabled,
        offerCurrent: value.offerCurrent,
        offerRevision: value.offerRevision as number,
        offerStatus: value.offerStatus as ExistingProviderPremiumMigrationOfferState['offerStatus'],
        approvedAt: optionalString(value.approvedAt),
        approvedByEmail: optionalString(value.approvedByEmail),
    };
};

// Root explicitly controls when a validated migration offer becomes visible to the provider's Admins.
export const setExistingProviderPremiumMigrationOfferEnabled = async (
    healthcareProviderId: string,
    enabled: boolean,
    validationToken?: string
): Promise<ExistingProviderPremiumMigrationOfferState> => {
    const callable = httpsCallable<{
        healthcareProviderId: string;
        enabled: boolean;
        validationToken?: string;
    }, unknown>(
        getFunctions(),
        'setExistingProviderPremiumMigrationOfferEnabled'
    );
    const result = await callable({
        healthcareProviderId,
        enabled,
        ...(enabled && validationToken ? { validationToken } : {}),
    });
    const value = result.data;
    if (!isRecord(value) || value.healthcareProviderId !== healthcareProviderId ||
        value.offerEnabled !== enabled || typeof value.offerCurrent !== 'boolean' ||
        !Number.isInteger(value.offerRevision) || (value.offerRevision as number) < 0 ||
        (value.offerStatus !== undefined &&
            !premiumMigrationOfferStatuses.includes(value.offerStatus as typeof premiumMigrationOfferStatuses[number]))) {
        throw new Error('The Premium migration offer could not be updated. Please reload and try again.');
    }
    return {
        healthcareProviderId,
        offerEnabled: value.offerEnabled,
        offerCurrent: value.offerCurrent,
        offerRevision: value.offerRevision as number,
        offerStatus: value.offerStatus as ExistingProviderPremiumMigrationOfferState['offerStatus'],
        approvedAt: optionalString(value.approvedAt),
        approvedByEmail: optionalString(value.approvedByEmail),
    };
};

// Create Checkout only after the operator has selected an existing Admin and sufficient capacity.
export const createExistingProviderPremiumCheckout = async (
    healthcareProviderId: string,
    request: ExistingProviderPremiumCheckoutRequest
): Promise<ExistingProviderPremiumCheckoutResponse> => {
    const callable = httpsCallable<
        ExistingProviderPremiumCheckoutRequest & { healthcareProviderId: string },
        ExistingProviderPremiumCheckoutResponse
    >(getFunctions(), 'createExistingProviderPremiumCheckout');
    const result = await callable({ healthcareProviderId, ...request });
    const value = result.data;
    if (!value || value.healthcareProviderId !== healthcareProviderId ||
        value.status !== 'requires_checkout' ||
        typeof value.checkoutIntentId !== 'string' || !value.checkoutIntentId ||
        typeof value.checkoutUrl !== 'string' || !/^https:\/\//i.test(value.checkoutUrl)) {
        throw new Error('Stripe Checkout could not be opened. Please reload and try again.');
    }
    return value;
};

// Firebase uses these placeholder messages when a callable fails before returning a safe HttpsError.
const isGenericCallableErrorMessage = (message: string): boolean => {
    const normalized = message.trim().toLowerCase();
    return normalized === 'internal' ||
        normalized === 'unknown' ||
        normalized === 'functions/internal' ||
        normalized === 'functions/unknown' ||
        /^firebase: error \(functions\/(?:internal|unknown)\)\.?$/.test(normalized);
};

export const getSaasErrorMessage = (
    error: unknown,
    fallback = 'Something went wrong. Please try again.'
): string => {
    const message = error instanceof Error ? error.message.trim() : '';
    if (message && !isGenericCallableErrorMessage(message)) return message;
    return fallback;
};
